import { ChevronRight, Image, PicturePlaceholder } from '../../../../modules/design-system';
import { EmptyState } from '../EmptyState';
import type { IframePoolShipping } from '../../../../modules/api';
import { PHOTO_BG_STYLE_DELIVERY } from '../photoBgStyle';
import { ShippingStatusChip } from '../shipping/ShippingStatusChip';
import { formatShippingDate } from '../shipping/formatShippingDate';

const SKELETON_COUNT = 4;

function ShippingSkeleton() {
  return (
    <div
      data-co-component="MyPacksSkeleton"
      className="h-[135px] w-full rounded-[18px] animate-pulse"
      style={{
        background: 'var(--co-MyPacksSkeleton-fill, rgba(255,255,255,0.05))',
        borderStyle: 'solid',
        borderColor: 'var(--co-MyPacksSkeleton-borderColor, rgba(171,174,179,0.24))',
        borderWidth: 'var(--co-MyPacksSkeleton-borderWidth, 2px)',
      }}
    />
  );
}

export interface DeliveriesTabProps {
  /** Feed state — the pagination hook lives in MyPacksPage (like the
   *  Packs / Cards feeds) so the list survives tab switches and only
   *  skeletons when it's genuinely empty. */
  shippings: IframePoolShipping[];
  /** First-ever load of this feed — the only state that gets a skeleton. A
   *  reload behind an already-rendered tab must not, see useInfinitePagination. */
  initialLoading: boolean;
  loadingMore: boolean;
  reachedEnd: boolean;
  error: string | null;
  /** Error-state "Try again" CTA. */
  onRetry: () => void;
  /** Sentinel ref from useInfinitePagination — attach to a node at the
   *  end of the grid to drive infinite scroll. */
  sentinelRef: (node: Element | null) => void;
  /** Card click → open the Delivery Show page for that shipping id. */
  onOpenDelivery: (shippingId: number) => void;
  /** Empty-state "Buy packs" — back to the lobby. */
  onBrowse: () => void;
}

/**
 * Deliveries tab — the user's pool_shippings, one row each (sleek partner
 * mockup): a card thumbnail, "N items", the date, the status pill and a
 * chevron. The status pill keeps its colours (green shipped / delivered,
 * dim canceled, neutral pending).
 */
export function DeliveriesTab({
  shippings,
  initialLoading,
  loadingMore,
  reachedEnd,
  error,
  onRetry,
  sentinelRef,
  onOpenDelivery,
  onBrowse,
}: DeliveriesTabProps) {
  if (initialLoading) {
    // The list's own one-column layout, so the rows don't jump on load.
    return (
      <div className="mx-auto flex w-full max-w-[640px] flex-col gap-3">
        {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
          <ShippingSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (error && shippings.length === 0) {
    return (
      <EmptyState
        title="Couldn't load deliveries"
        description={error}
        cta="Try again"
        onAction={onRetry}
      />
    );
  }

  if (shippings.length === 0) {
    return (
      <EmptyState
        title="No cards found"
        description="When cards are shipped, they'll appear here."
        cta="Buy packs"
        onAction={onBrowse}
      />
    );
  }

  return (
    <>
      <ul className="mx-auto m-0 flex w-full max-w-[640px] list-none flex-col gap-3 p-0">
        {shippings.map((s) => (
          <li key={s.id}>
            <DeliveryCard
              shipping={s}
              onClick={() => onOpenDelivery(s.id)}
            />
          </li>
        ))}
        {loadingMore &&
          Array.from({ length: SKELETON_COUNT }).map((_, i) => (
            <li key={`sk-${i}`}>
              <ShippingSkeleton />
            </li>
          ))}
      </ul>
      {!reachedEnd && <div ref={sentinelRef} aria-hidden className="h-px" />}
    </>
  );
}

interface DeliveryCardProps {
  shipping: IframePoolShipping;
  onClick: () => void;
}

function DeliveryCard({ shipping, onClick }: DeliveryCardProps) {
  const count = shipping.poolItemsCount;
  // One card per delivery (partner Figma 2026-10-08): the row names it; a
  // multi-card or older delivery falls back to the count.
  const label = count === 1 && shipping.previewTitle ? shipping.previewTitle : `${count} item${count === 1 ? '' : 's'}`;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Open delivery #${shipping.id}`}
      data-co-component="MyPacksDeliveryCard"
      className="
        flex w-full items-center gap-4 p-4 text-left
        rounded-[18px] border border-solid
        bg-[var(--co-MyPacksDeliveryCard-bg,#1A1B1F)]
        border-[var(--co-MyPacksDeliveryCard-borderColor,rgba(255,255,255,0.01))]
        text-[var(--co-MyPacksDeliveryCard-titleColor,#FFFFFF)]
        transition-transform active:scale-[0.99]
      "
      style={{ fontFamily: 'var(--co-MyPacksDeliveryCard-fontFamily), Inter, system-ui, sans-serif' }}
    >
      <Thumbnail imageUrl={shipping.previewImageUrl} />
      <div className="flex min-w-0 flex-1 flex-col items-start gap-1.5">
        <span className="flex flex-col">
          <span className="text-[12px] leading-[16px] text-[var(--co-MyPacksDeliveryCard-dateColor,#6D707C)]">
            {formatShippingDate(shipping.createdAt)}
          </span>
          <span className="line-clamp-3 text-[16px] font-semibold leading-[20px] tracking-[-0.31px]">{label}</span>
        </span>
        <ShippingStatusChip status={shipping.status} label={shipping.humanizedStatus} settleSummary={shipping.settleSummary} />
      </div>
      <ChevronRight size={16} className="shrink-0 text-[var(--co-MyPacksDeliveryCard-dateColor,#6D707C)]" />
    </button>
  );
}

/** Portrait thumbnail of the shipment's first card, or a picture glyph until
 *  the API ships a preview. */
function Thumbnail({ imageUrl }: { imageUrl: string | null }) {
  return (
    <div className="relative h-[101px] w-[60px] shrink-0 overflow-hidden rounded-[4.3px] [box-shadow:var(--co-MyPacksDeliveryCard-photoShadow,0_4px_16px_2px_rgba(0,0,0,0.2))]" style={PHOTO_BG_STYLE_DELIVERY}>
      {imageUrl ? (
        <Image src={imageUrl} alt="" className="absolute inset-0" imgClassName="absolute inset-0 h-full w-full object-cover" />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center bg-[var(--co-MyPacksDeliveryCard-photoPlaceholderFill,rgba(255,255,255,0.04))]">
          <PicturePlaceholder size={22} strokeWidth={1.5} />
        </div>
      )}
    </div>
  );
}

